/**
 * Text track runtime (W1-05a engine-eval; rpe-format.md F§9.3, R§3.6, DESIGN.md §6.8).
 *
 * `textEvents` become keyframes exactly like numeric lists (two per event, list order, `""` prepended at 0 s when
 * the first event does not start at beat 0) and are evaluated with Phira's cursor rules; the interpolation is
 * Phira's string tween (`Ease.stringTween`: `%P%` counters, type-on / erase, extend / shrink, switch) applied to the
 * eased progress. Two Phira details matter:
 *  - between events the end keyframe's hold tween gives progress 0, so the string tween of (end, next start, 0)
 *    is shown: e.g. "%P%100" followed by "%P%0" shows "100" (the `%P%` marks stripped);
 *  - AFTER THE LAST keyframe the raw end string is returned unprocessed, so a final "%P%100" shows literally.
 * Phira panics where a reveal/erase overshoots (Back/Elastic); the preview clamps instead (the checker reports it).
 *
 * Internal API: `TextTrack.fromEvents(events, env)`, `track.at(t)`, `track.atDetailed(t)` (case + panic flag).
 */

import { stringTween, stringTweenDetailed, type StringTweenResult } from '../ease/stringTween';
import { eventTimeline, needsDefault, type EvalEnv, type EventLike, type Timeline } from './anim';

export interface TextSample {
  readonly text: string;
  /** Phira would panic here (reveal/erase sliced past the end); `text` is clamped. */
  readonly panics: boolean;
  /** The string-tween case, or null after the last keyframe / on a held keyframe value. */
  readonly case: StringTweenResult['case'] | null;
}

export class TextTrack {
  readonly timeline: Timeline;
  readonly values: readonly string[];

  private constructor(timeline: Timeline, values: readonly string[]) {
    this.timeline = timeline;
    this.values = values;
  }

  static fromEvents(events: ReadonlyArray<EventLike<string>>, env: EvalEnv): TextTrack {
    const withDefault = needsDefault(events);
    const tl = eventTimeline(events, env, withDefault);
    const values: string[] = [];
    if (withDefault) values.push('');
    for (const e of events) values.push(e.start, e.end);
    return new TextTrack(tl, values);
  }

  /** The text Phira shows at t. */
  at(t: number): string {
    const tl = this.timeline;
    if (tl.n === 0) return '';
    const i = tl.cursorAt(t);
    const x = this.values[i] ?? '';
    if (i + 1 >= tl.n) return x;
    return stringTween(x, this.values[i + 1] ?? '', tl.yAt(i, t));
  }

  atDetailed(t: number): TextSample {
    const tl = this.timeline;
    if (tl.n === 0) return { text: '', panics: false, case: null };
    const i = tl.cursorAt(t);
    const x = this.values[i] ?? '';
    if (i + 1 >= tl.n) return { text: x, panics: false, case: null };
    const y = this.values[i + 1] ?? '';
    const r = stringTweenDetailed(x, y, tl.yAt(i, t));
    return { text: r.text, panics: r.panics, case: r.case };
  }

  usesBezier(): boolean {
    return this.timeline.usesBezier();
  }
}
